// src/services/referralService.js
import { apiClient } from "../../config/api.config";

const ReferralService = {
  /**
   * Fetch referral statistics with optional time range parameter
   */
  getReferralStats: async (timeRange = "month") => {
    try {
      const response = await apiClient.get(`/admin/referralstats`, {
        params: { timeRange },
      });

      if (response.data?.success && response.data?.data) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get referral reward configuration
   */
  getReferralRewardConfig: async () => {
    try {
      const response = await apiClient.get(`/referrals/admin/referral/config`);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return handleError(error);
    }
  },
  getReferralRewardConfigs: async (status = "ALL") => {
    try {
      const response = await apiClient.get(
        `/referrals/admin/referral/reward-configs`,
        {
          params: { status },
        }
      );
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return handleError(error);
    }
  },
  /**
   * Create or update reward configuration
   * @param {object} configData - Reward configuration data
   * @returns {Promise<object>} - Updated reward configuration
   */
  createOrUpdateRewardConfig: async (configData) => {
    try {
      const response = await apiClient.post(
        "/referrals/admin/referral/reward-config",
        configData
      );

      if (response.data?.success) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get top referrers
   */
  getTopReferrers: async (limit = 10) => {
    try {
      const response = await apiClient.get(`/admin/referral/top-referrers`, {
        params: { limit },
      });
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get recent referral activity
   */
  getRecentActivity: async (timeRange = "month") => {
    try {
      const response = await apiClient.get(`/admin/referral/recent-activity`, {
        params: { timeRange },
      });
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get tracked referrals list with filters
   */
  getTrackedReferrals: async (page = 1, limit = 20, filters = {}) => {
    try {
      const response = await apiClient.get(
        `/admin/referral/tracked-referrals`,
        {
          params: {
            page,
            limit,
            ...filters,
          },
        }
      );
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Fetch referrals with pagination, sorting, and filtering
   */
  getReferrals: async (page, limit, sortBy, sortOrder, filters) => {
    try {
      const query = {
        page,
        limit,
        sortBy,
        sortOrder,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== "")
        ),
      };

      const response = await apiClient.get(
        `/referrals/admin/referral-tracking`,
        {
          params: query,
        }
      );

      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Process qualification for a referral
   */
  qualifyReferral: async (trackingId) => {
    try {
      const response = await apiClient.patch(
        `/referrals/admin/qualify/${trackingId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

/**
 * Generic error handler for formatted response
 */
function handleError(error) {
  if (error.response) {
    return {
      success: false,
      error:
        error.response.data?.message ||
        error.response.statusText ||
        "Unknown server error",
    };
  } else if (error.request) {
    return { success: false, error: "Network error: No server response" };
  } else {
    return { success: false, error: `Error: ${error.message}` };
  }
}

export default ReferralService;
